import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import Table from '../../components/Table';
import PageHeader from '../../components/PageHeader';
import { toastSuccess, toastError } from '../../utils/toast';
import { swalConfirm } from '../../utils/swal';
import { FaUserPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { proyectService } from '../../services/proyectService';
import { formatdateWithTime } from '../../utils/helpers';

const Proyects = () => {
  const [proyects, setProyects] = useState([]);
  const navigate = useNavigate();
  
  const handleAddProyect = () => {
    navigate('/proyects/add', { state: { isEdit: false, proyectEdit: null } });
  };

  // Definición de las columnas de la tabla
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'name',
      title: 'Nombre',
      sortable: true,
    },
    {
      key: 'description',
      title: 'Descripción',
      sortable: true,
    },
    {
      key: 'project_types',
      title: 'Tipo de Proyecto',
      sortable: true,
      render: (value) => value.name
    },
    {
      key: 'project_manager',
      title: 'Gerente de Proyecto',
      sortable: true,
      render: (value) => value.name
    },
    {
      key: 'platform',
      title: 'Plataforma',
      sortable: true,
      render: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : '—',
    },
    {
      key: 'status',
      title: 'Estado',
      sortable: true,
      render: (value) => (
        <span
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={value === 'active'
            ? { backgroundColor: 'rgba(34,197,94,0.12)', color: '#4ADE80' }
            : { backgroundColor: 'rgba(255,255,255,0.06)', color: '#9BA3B8' }
          }
        >
          {value === 'active' ? 'Activo' : value === 'inactive' ? 'Inactivo' : value || '—'}
        </span>
      ),
    },
    {
      key: 'created_at',
      title: 'Creado',
      sortable: true,
      render: (value) => formatdateWithTime(value)
    },
    {
      key: 'actions',
      title: 'Acciones',
      sortable: false,
      render: (_, proyect) => (
        <div className="flex space-x-2">
          <button 
            disabled={proyect.status === 'inactive'}
            onClick={() => handleEdit(proyect)} 
            className={`rounded-full ${proyect.status === 'inactive' ? 'bg-gray-500' : 'bg-blue-500 hover:transition-all hover:bg-blue-600'} text-white p-[8px] items-center justify-center flex gap-1`}
            title="Editar"
          >
            <FaEdit />
          </button>
          <button
            disabled={proyect.status === 'inactive'}
            onClick={() => handleDelete(proyect.id)} 
            className={`rounded-full ${proyect.status === 'inactive' ? 'bg-gray-500' : 'bg-red-500 hover:transition-all hover:bg-red-600'} text-white p-[8px] items-center justify-center flex gap-1`}
            title="Eliminar"
          >
            <FaTrash />
          </button>
          {/*<button 
            onClick={() => handleStatusChange(proyect.id)} 
            className="rounded-full bg-green-600 text-white p-[8px] items-center justify-center flex hover:transition-all hover:bg-green-700"
            title="Cambiar estado"
          >
            {proyect.status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
          </button>*/}
        </div>
      )
    },
  ];

  // Opciones de la tabla
  const tableOptions = {
    pagination: true,
    pageSize: 5,
    pageSizeOptions: [5, 10, 20],
    showSearch: true,
    searchPlaceholder: 'Buscar proyectos...',
    emptyMessage: 'No hay proyectos disponibles',
  };

  useEffect(() => {
    fetchProyects();
  }, []);

  const fetchProyects = async () => {
    try {
      const proyects = await proyectService.getAll();
      console.log(proyects);
      setProyects(proyects);
    } catch (error) {
      console.error('Error al obtener proyectos:', error.message);
    }
  };

  const handleEdit = (proyect) => {
    navigate('/proyects/edit', { state: { isEdit: true, proyectEdit: proyect } });
  };

  const handleDelete = async (proyectId) => {
    const { isConfirmed } = await swalConfirm(
      '¿Eliminar proyecto?',
      '¿Está seguro de que desea eliminar este proyecto? Esta acción no se puede deshacer.',
      'Sí, eliminar',
      {
        // Aseguramos que no se muestren campos de formulario ni botones adicionales
        input: null,
        html: null,
        footer: null,
        showDenyButton: false,
        denyButtonText: undefined
      }
    );
    
    if (isConfirmed) {
      await proyectService.deleteProyect(proyectId);
      fetchProyects();
      toastSuccess('Proyecto eliminado correctamente');
    }
  };

  const handleStatusChange = async (proyectId) => {
    const { isConfirmed } = await swalConfirm(
      '¿Cambiar estado?',
      '¿Está seguro de que desea cambiar el estado de este proyecto? Esta acción no se puede deshacer.',
      'Sí, cambiar',
      {
        // Aseguramos que no se muestren campos de formulario ni botones adicionales
        input: null,
        html: null,
        footer: null,
        showDenyButton: false,
        denyButtonText: undefined
      }
    );
    
    if (isConfirmed) {
      const status = proyects.find(proyect => proyect.id === proyectId).status == "active" ? "inactive" : "active";
      await proyectService.toggleProyectStatus(proyectId, status);
      fetchProyects();
      toastSuccess('Estado del cliente cambiado correctamente');
    }
  };

  return (
    <div>
      <PageHeader title="Proyectos" subtitle="Administra los proyectos y su configuración">
        <Button variant="warning" onClick={handleAddProyect} leftIcon={<FaUserPlus size={12} />}>
          Agregar Proyecto
        </Button>
      </PageHeader>
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16,1,0.3,1] }}
        style={{
          background: 'linear-gradient(160deg, #161731 0%, #12132a 55%, #0f1024 100%)',
          border: '1px solid rgba(255,255,255,0.07)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        <Table data={proyects} columns={columns} options={tableOptions} />
      </motion.div>
    </div>
  )
}

export default Proyects
